import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Addon } from '@domain/addon';
import { Admission } from '@domain/admission';
import { formatDDP, formatOfflinePrice, totalOnlinePrice } from '@utils/calculate-booking-price';
import classNames from 'classnames';

import { AddOnsSummary } from './AddOnsSummary';
import { AdmissionSummary } from './AdmissionSummary';
import { ButtonExpand } from './ButtonExpand';
import { TotalPriceSummary } from './TotalPriceSummary';

export interface SummaryTableHeaderProps {
  addons?: Addon[];
  quantity: number;
  offer: Admission;
  fullName?: string;
  offerName: string;
  formAddons?: string[];
  isInvitation?: boolean;
}

export const SummaryTableBody = ({
  addons,
  quantity,
  offer,
  isInvitation,
  fullName,
  offerName,
  formAddons,
}: SummaryTableHeaderProps) => {
  const { i18n } = useTranslation();
  const selectedAddons = addons?.filter((addon: Addon) => formAddons?.includes(addon.id)) ?? [];
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  const onlinePrice = totalOnlinePrice({
    offer,
    quantity,
    addons,
    selectedAddons: formAddons,
    locale: i18n.language,
  });
  const offlinePrice = formatOfflinePrice({ offer, quantity });

  const ddpPrice = formatDDP({ offer, quantity });
  const showAddons = !!formAddons && selectedAddons.length > 0;

  return (
    <div className='w-full bg-surface-secondary py-4 px-6 rounded-xl'>
      <div
        className={classNames('grid grid-cols-2 justify-between gap-2 overflow-hidden transition-all relative', {
          'max-h-40': !isSummaryExpanded,
          'max-h-[1000px]': isSummaryExpanded,
        })}>
        <AdmissionSummary offer={offer} quantity={quantity} name={isInvitation && fullName ? fullName : offerName} />
        <AddOnsSummary
          isVisible={showAddons}
          addons={selectedAddons}
          quantity={quantity}
          currency={offer.price.currency}
        />
        {!isSummaryExpanded && showAddons && (
          <div className='absolute bottom-0 left-0 w-full h-16 bg-linear-to-t from-neutral-2 to-transparent dark:from-neutral-80' />
        )}
      </div>

      <ButtonExpand
        isVisible={showAddons}
        isSummaryExpanded={isSummaryExpanded}
        setIsSummaryExpanded={setIsSummaryExpanded}
      />

      <TotalPriceSummary
        isVisible={!!offer?.price.offlinePrice || !!offer?.price.onlinePrice}
        onlinePrice={onlinePrice}
        offlinePrice={offer?.price.offlinePrice ? offlinePrice : 0}
        ddpPrice={offer?.settings.italyRegulation?.dirittoPrevendita ? ddpPrice : 0}
      />
    </div>
  );
};
